import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { X, Link2, Copy, Check, Globe, Lock, RefreshCw, Eye, Share2 } from 'lucide-react';
import { selectAuthToken } from '../../features/auth/authSlice';
import shareService, { shareUrl } from '../../features/sharing/shareService';
import { track } from '../../analytics/analytics';
import './ShareReportDialog.css';

const DAY_MS = 24 * 60 * 60 * 1000;

const formatDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const canNativeShare = () => typeof navigator !== 'undefined' && typeof navigator.share === 'function';

const EXPIRY_UNITS = [
  { value: 'hours', label: 'Hours', max: 72 },
  { value: 'days', label: 'Days', max: 365 }, // backend allows up to 366
];
const unitMax = (unit) => EXPIRY_UNITS.find((u) => u.value === unit)?.max ?? 1;

const DEFAULT_EXPIRY = { never: false, amount: 1, unit: 'days' };

const isValidExpiry = (v) =>
  v.never || (Number.isInteger(v.amount) && v.amount >= 1 && v.amount <= unitMax(v.unit));

/** ISO expiry for a picker value, or null for never. */
const expiryToIso = (v) => {
  if (v.never) return null;
  const d = new Date();
  const unitMs = v.unit === 'hours' ? 60 * 60 * 1000 : DAY_MS;
  d.setTime(d.getTime() + v.amount * unitMs);
  return d.toISOString();
};

/**
 * "Expires after [- 1 +] [Hours|Days]" or "Never", 1Password-style.
 * Buttons rather than <select>/<input type=date>: iOS Safari mishandled both.
 * Purely controlled -- the parent decides when to save.
 */
function ExpiryPicker({ value, onChange, disabled }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const max = unitMax(value.unit);
  const amount = Number.isInteger(value.amount) ? value.amount : 1;

  return (
    <div className="share-expiry">
      <div className="share-expiry-mode" role="group" aria-label="Link expires">
        <button
          type="button"
          className={`share-expiry-btn${!value.never ? ' selected' : ''}`}
          aria-pressed={!value.never}
          onClick={() => set({ never: false })}
          disabled={disabled}
        >
          After
        </button>
        <button
          type="button"
          className={`share-expiry-btn${value.never ? ' selected' : ''}`}
          aria-pressed={value.never}
          onClick={() => set({ never: true })}
          disabled={disabled}
        >
          Never
        </button>
      </div>

      {!value.never && (
        <div className="share-expiry-after">
          <div className="share-stepper">
            <button
              type="button"
              className="share-stepper-btn"
              onClick={() => set({ amount: Math.max(1, amount - 1) })}
              disabled={disabled || amount <= 1}
              aria-label="Less"
            >
              −
            </button>
            <input
              className="share-stepper-input"
              inputMode="numeric"
              pattern="[0-9]*"
              value={value.amount === '' ? '' : String(value.amount)}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 3);
                set({ amount: digits === '' ? '' : Math.min(max, Number(digits)) });
              }}
              onBlur={() => { if (!Number.isInteger(value.amount) || value.amount < 1) set({ amount: 1 }); }}
              disabled={disabled}
              aria-label="Amount"
            />
            <button
              type="button"
              className="share-stepper-btn"
              onClick={() => set({ amount: Math.min(max, amount + 1) })}
              disabled={disabled || amount >= max}
              aria-label="More"
            >
              +
            </button>
          </div>

          <div className="share-expiry-units" role="group" aria-label="Unit">
            {EXPIRY_UNITS.map((u) => (
              <button
                key={u.value}
                type="button"
                className={`share-expiry-btn${value.unit === u.value ? ' selected' : ''}`}
                aria-pressed={value.unit === u.value}
                onClick={() => set({ unit: u.value, amount: Math.min(amount, u.max) })}
                disabled={disabled}
              >
                {u.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older mobile browsers: fall back to a hidden textarea.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
};

/**
 * Google-Docs-style sharing for one report: a single link that is either
 * Public (anyone with the link) or Locked (link + PIN). Changing access never
 * changes the link; "Stop sharing" kills it for good.
 */
function ShareReportDialog({ jobId, reportTitle, onClose }) {
  const authToken = useSelector(selectAuthToken);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [confirmStop, setConfirmStop] = useState(false);

  // Settings for a link that doesn't exist yet.
  const [newAccess, setNewAccess] = useState('public');
  const [newExpiry, setNewExpiry] = useState(DEFAULT_EXPIRY);

  // An existing link shows its expiry as one line; the picker only opens
  // while the owner is changing it, and nothing saves until they tap Save.
  const [editingExpiry, setEditingExpiry] = useState(false);
  const [editExpiry, setEditExpiry] = useState(DEFAULT_EXPIRY);

  useEffect(() => {
    let cancelled = false;
    shareService.getShare(jobId, authToken)
      .then((s) => { if (!cancelled) setShare(s); })
      .catch(() => { if (!cancelled) setError("Couldn't load sharing settings. Please try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId, authToken]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Stop the report behind the dialog from scrolling on phones.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const save = useCallback(async (settings) => {
    setSaving(true);
    setError('');
    try {
      setShare(await shareService.saveShare(jobId, settings, authToken));
      return true;
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't update sharing. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [jobId, authToken]);

  const handleCreate = async () => {
    if (!isValidExpiry(newExpiry)) return;
    if (await save({ access: newAccess, expires_at: expiryToIso(newExpiry) })) {
      track('share_link_created', {
        access: newAccess,
        expiry: newExpiry.never ? 'never' : `${newExpiry.amount}_${newExpiry.unit}`,
      });
    }
  };

  const startEditingExpiry = () => {
    setEditExpiry(share.expires_at ? DEFAULT_EXPIRY : { ...DEFAULT_EXPIRY, never: true });
    setEditingExpiry(true);
  };

  const handleSaveExpiry = async () => {
    if (!isValidExpiry(editExpiry)) return;
    if (await save({ access: share.access, expires_at: expiryToIso(editExpiry) })) {
      setEditingExpiry(false);
    }
  };

  // The phone's own share sheet: WhatsApp, Telegram, Messages, email, ...
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: 'Content check report',
        text: `Content check report for "${reportTitle}"`,
        url,
      });
      track('share_link_sent', { method: 'share_sheet', access: share.access });
    } catch (err) {
      // AbortError = the user closed the share sheet; nothing to do.
      if (err?.name !== 'AbortError') handleCopy('link', url);
    }
  };

  const handleStop = async () => {
    setSaving(true);
    setError('');
    try {
      await shareService.revokeShare(jobId, authToken);
      track('share_link_revoked');
      setShare(null);
      setConfirmStop(false);
      setEditingExpiry(false);
    } catch {
      setError("Couldn't stop sharing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (what, text) => {
    if (await copyText(text)) {
      if (what === 'link') track('share_link_sent', { method: 'copy', access: share?.access });
      setCopied(what);
      setTimeout(() => setCopied(''), 2000);
    }
  };

  const url = share ? shareUrl(share.share_id) : '';
  const isLocked = share?.access === 'locked';
  // The PIN deliberately stays out of the shared message: a forwarded
  // message would then carry both halves of the lock.

  // Portal to <body>: rendered inside the dashboard layout, the dialog was
  // trapped below the site header, which then covered its close button.
  return createPortal(
    <div className="share-overlay" onClick={onClose}>
      <div
        className="share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Pinned while the body scrolls, so Close is always reachable. */}
        <div className="share-topbar">
          <div className="share-topbar-text">
            <h2 id="share-title" className="share-title">Share report</h2>
            <p className="share-subtitle">{reportTitle}</p>
          </div>
          <button className="share-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="share-body">

        {loading ? (
          <div className="share-loading">Loading…</div>
        ) : !share ? (
          /* ---------- No link yet ---------- */
          <>
            <p className="share-intro">
              Create a read-only link to this report. Viewers see the results only — never
              your video, account or other scans.
            </p>

            <fieldset className="share-access">
              <legend className="share-label">Who can open it</legend>
              <AccessOption
                value="public" current={newAccess} onChange={setNewAccess}
                icon={<Globe size={18} />} title="Anyone with the link"
                hint="Easiest to share, e.g. on WhatsApp"
              />
              <AccessOption
                value="locked" current={newAccess} onChange={setNewAccess}
                icon={<Lock size={18} />} title="Link + PIN"
                hint="Viewers also need a 6-digit PIN you send them"
              />
            </fieldset>

            <div className="share-label">Link expires</div>
            <ExpiryPicker value={newExpiry} onChange={setNewExpiry} disabled={saving} />

            {error && <p className="share-error">{error}</p>}

            <button className="share-primary" onClick={handleCreate} disabled={saving}>
              <Link2 size={18} />
              {saving ? 'Creating link…' : 'Create link'}
            </button>
          </>
        ) : (
          /* ---------- Link exists ---------- */
          <>
            <div className="share-link-row">
              <input className="share-link-input" value={url} readOnly onFocus={(e) => e.target.select()} aria-label="Share link" />
              <button className="share-icon-btn" onClick={() => handleCopy('link', url)}>
                {copied === 'link' ? <Check size={16} /> : <Copy size={16} />}
                {copied === 'link' ? 'Copied' : 'Copy'}
              </button>
            </div>

            {canNativeShare() && (
              <button className="share-native" onClick={handleNativeShare}>
                <Share2 size={18} />
                Share link…
              </button>
            )}

            <fieldset className="share-access" disabled={saving}>
              <legend className="share-label">Who can open it</legend>
              <AccessOption
                value="public" current={share.access} onChange={(v) => save({ access: v })}
                icon={<Globe size={18} />} title="Anyone with the link"
                hint="No PIN needed"
              />
              <AccessOption
                value="locked" current={share.access} onChange={(v) => save({ access: v })}
                icon={<Lock size={18} />} title="Link + PIN"
                hint="The link alone won't open it"
              />
            </fieldset>

            {isLocked && (
              <div className="share-pin-box">
                <div>
                  <div className="share-label">PIN</div>
                  <div className="share-pin">{share.pin}</div>
                  <div className="share-pin-hint">Send the PIN separately from the link.</div>
                </div>
                <div className="share-pin-actions">
                  <button className="share-icon-btn" onClick={() => handleCopy('pin', share.pin)}>
                    {copied === 'pin' ? <Check size={16} /> : <Copy size={16} />}
                    {copied === 'pin' ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    className="share-icon-btn"
                    onClick={() => save({ access: 'locked', regenerate_pin: true })}
                    disabled={saving}
                    title="Old PIN stops working immediately"
                  >
                    <RefreshCw size={16} />
                    New PIN
                  </button>
                </div>
              </div>
            )}

            <div className="share-label">Link expires</div>
            {!editingExpiry ? (
              <div className="share-expiry-summary">
                <span className="share-expiry-status">
                  {share.expires_at ? formatDateTime(share.expires_at) : 'Never'}
                </span>
                <button type="button" className="share-icon-btn" onClick={startEditingExpiry} disabled={saving}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <ExpiryPicker value={editExpiry} onChange={setEditExpiry} disabled={saving} />
                <div className="share-expiry-apply">
                  <button
                    type="button"
                    className="share-text-btn"
                    onClick={() => setEditingExpiry(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="share-icon-btn"
                    onClick={handleSaveExpiry}
                    disabled={saving || !isValidExpiry(editExpiry)}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </>
            )}

            <p className="share-views">
              <Eye size={14} />
              {share.view_count > 0
                ? `Opened ${share.view_count} ${share.view_count === 1 ? 'time' : 'times'}` +
                  (share.last_viewed_at ? `, last on ${formatDay(share.last_viewed_at)}` : '')
                : 'Not opened yet'}
            </p>

            {error && <p className="share-error">{error}</p>}

            <div className="share-footer">
              <button className="share-done" onClick={onClose}>Done</button>
              {confirmStop ? (
                <div className="share-confirm">
                  <span>The link will stop working for everyone.</span>
                  <div className="share-confirm-actions">
                    <button className="share-text-btn" onClick={() => setConfirmStop(false)} disabled={saving}>
                      Cancel
                    </button>
                    <button className="share-danger" onClick={handleStop} disabled={saving}>
                      {saving ? 'Stopping…' : 'Stop sharing'}
                    </button>
                  </div>
                </div>
              ) : (
                <button className="share-text-btn danger" onClick={() => setConfirmStop(true)}>
                  Stop sharing
                </button>
              )}
            </div>
          </>
        )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function AccessOption({ value, current, onChange, icon, title, hint }) {
  const selected = current === value;
  return (
    <label className={`share-access-option${selected ? ' selected' : ''}`}>
      <input
        type="radio"
        name="share-access"
        value={value}
        checked={selected}
        onChange={() => { if (!selected) onChange(value); }}
      />
      <span className="share-access-icon">{icon}</span>
      <span className="share-access-text">
        <span className="share-access-title">{title}</span>
        <span className="share-access-hint">{hint}</span>
      </span>
    </label>
  );
}

export default ShareReportDialog;
